import AuthStream from "@irati/auth/streams/auth"
import UnauthedStream from "@irati/auth/streams/unauthed"
import ObserveWithPriority from "@irati/auth/helpers/auth/ObserveWithPriority"
import {User, Business, DealsPackage, ProductPermissions} from "@irati/types"
import {Auth} from "@irati/auth/types"
import {get} from "lodash"
import {ClientDetailsType} from "../../services/apollo/helpers/getClientDetails/interface"
import env from "../../env"
import Throttle from "../Throttle"
import getClientDetails from "@irati/utils/services/apollo/helpers/getClientDetails"

export default abstract class Intercom {
  constructor() {
    this.setup()

    let isIdentitySet = false
    const throttle = new Throttle()

    setTimeout(() => {
      new ObserveWithPriority<Pick<Auth, "user" | "business" | "dealPackage" | "productPermissions">>({
        streams: [AuthStream, UnauthedStream],
        key: "user"
      }).subscribe(async (data) => {
        if (data && data.user && data.business) {
          if (!isIdentitySet) {
            await throttle.add(() => this.setIdentity(data.user, data.business, data.dealPackage, data.productPermissions))
            isIdentitySet = true
          } else {
            throttle.add(() => this.updateIdentity(data.user, data.business, data.dealPackage, data.productPermissions))
          }
        } else {
          isIdentitySet = false
          this.clearIdentity()
        }
      })
    })
  }

  protected async getClientVersion() {
    const clientDetails = await getClientDetails()
    const data: {[key: string]: string} = {}
    const key = `version_${clientDetails.name}`
    data[key] = clientDetailsToVersion(clientDetails)
    return data
  }

  async logError({errorData, ...metadata}: Pick<PBError, "errorCode" | "errorData" | "traceId"> & {[key: string]: string}) {
    const formattedErrorData = errorData?.errors?.[0]?.errorData || {}

    if (Object.keys(formattedErrorData).length) {
      metadata.errorData = JSON.stringify(errorData.errors[0].errorData)
    }

    try {
      const clientDetails = await getClientDetails()
      metadata.version = `${clientDetails.name} ${clientDetailsToVersion(clientDetails)}`
    } catch (e) {
      /* no-op */
    }

    return this.logEvent("loggedError", metadata)
  }

  protected abstract setup(): any

  protected abstract setIdentity(
    user: User,
    business: Business,
    dealPackage?: DealsPackage,
    productPermissions?: ProductPermissions
  ): Promise<any>
  protected abstract updateIdentity(
    user: User,
    business: Business,
    dealPackage?: DealsPackage,
    productPermissions?: ProductPermissions
  ): Promise<void>
  protected abstract clearIdentity(): void

  abstract hide(): void
  abstract show(): void

  abstract logEvent(eventName: string, data: any): any

  abstract addCustomMessage(message: string, url?: string): void
}

function clientDetailsToVersion(clientDetails: ClientDetailsType) {
  if (clientDetails.name === "web") {
    return `${clientDetails.version} (${env.BUNDLE_REF})`
  }
  return `${clientDetails.version}`
}

export function dealPackageToString(dealPackage?: DealsPackage) {
  if (dealPackage?.name) {
    return `${dealPackage.name}`
  }

  return "None"
}

export function productAccessData(productPermissions?: ProductPermissions) {
  const access = {
    "Tenant Wallet": !!get(productPermissions, "wallet.tenantWallet", false),
    "Reward Wallet": !!get(productPermissions, "wallet.rewardWallet", false),
    "User Wallet": !!get(productPermissions, "wallet.users", false),
    Recognition: !!get(productPermissions, "recognition", null),
    Medical: !!get(productPermissions, "medical", null),
    Perks: !!get(productPermissions, "perks", null),
    "Wellness Hub": !!get(productPermissions, "wellnessHub", null)
  }

  return Object.keys(access)
    .reduce((current, key) => {
      if (access[key]) {
        current.push(key)
      }
      return current
    }, [])
    .join(", ")
}

export function packageAddons(dealPackage?: DealsPackage) {
  return (dealPackage?.addons || []).map((t) => t.name).join(", ")
}
